import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TableData } from 'models/ai-chat/visualization.types';
import * as XLSX from 'xlsx';
import { useChat } from 'store/useChatStore';
import { TableRequest } from 'models/ai-chat/table-pagination';
import { useAppSelector } from 'redux/hooks';
import { RootState } from 'redux/store';
import AppliedFiltersDisplay from './AppliedFiltersDisplay';
import { FilterItem } from 'models/ai-chat/filter-request';
import Tooltip from '../tooltip';
import { LlmPayload } from './TableView';

interface TableFullViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: TableData;
  sortConfig: { column: number; direction: 'asc' | 'desc' } | null;
  onSort: (columnIndex: number) => void;
  filteredAndSortedRows: any[][];
  isNumericColumn: (columnIndex: number) => boolean;
  formatCellValue: (cell: any, columnIndex?: number) => any;
  onExport: () => void;
  request: TableRequest | null;
  onDataUpdate: (newData: TableData) => void;
  // New props for AppliedFiltersDisplay
  sqlQuery?: string;
  messageId?: string;
  onFilterValueChange?: (filter: any, newValue: string) => void;
  onFilterUpdateComplete?: (updatedData: any) => void;
  filterData?: FilterItem[];
  customerId?: string;
  onExportExcel?: () => void;
  onDownloadPDF?: () => void;
  llmPayload?: LlmPayload;
}

const TableFullViewModal: React.FC<TableFullViewModalProps> = ({
  isOpen,
  onClose,
  data,
  onSort,
  filteredAndSortedRows,
  isNumericColumn,
  formatCellValue,
  onExport,
  request,
  onDataUpdate,
  // New props for AppliedFiltersDisplay
  sqlQuery,
  messageId,
  onFilterValueChange,
  onFilterUpdateComplete,
  filterData,
  customerId,
  onExportExcel,
  onDownloadPDF,
  llmPayload
}) => {

  const { tableRows, loading } = useAppSelector((state: RootState) => state.chat);
  const { getTableDataRequest } = useChat();
  const [sortConfig, setSortConfig] = useState<{ column: number; direction: 'asc' | 'desc' } | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalContainer(document.body);
    }
  }, []);

  // Helper functions for image detection and rendering
  const isImageColumn = (columnIndex: number) => {
    if (!data?.headers?.[columnIndex]) return false;
    const header = data.headers[columnIndex].toString().toLowerCase();
    const imageKeywords = ['product_image', 'image', 'photo', 'picture', 'img', 'thumbnail'];
    return imageKeywords.some(keyword => header.includes(keyword));
  };

  // Helper function to get display name for a column header from llmPayload
  const getColumnDisplayName = useCallback((headerName: string): string => {
    // Debug log
    // console.log('getColumnDisplayName called:', { headerName, llmPayload, hasColumns: llmPayload?.columns?.length });
    
    if (!llmPayload?.columns || !Array.isArray(llmPayload.columns) || llmPayload.columns.length === 0) {
      return headerName;
    }
    
    const column = llmPayload.columns.find(
      (col) => col.name?.toUpperCase() === headerName?.toUpperCase()
    );
    
    return column?.displayName || headerName;
  }, [llmPayload]);

  // Helper function to check if a column is a measure based on llmPayload role
  const isMeasureColumn = useCallback((columnIndex: number): boolean => {
    if (!llmPayload?.columns || !Array.isArray(llmPayload.columns) || !data?.headers?.[columnIndex]) {
      return false;
    }
    
    const headerName = data.headers[columnIndex].toString();
    const column = llmPayload.columns.find(
      (col) => col.name?.toUpperCase() === headerName?.toUpperCase()
    );
    
    return column?.role === 'measure';
  }, [llmPayload, data?.headers]);

  // Helper function to get column alignment based on role from llmPayload
  const getColumnAlignment = useCallback((columnIndex: number): 'left' | 'right' | 'center' => {
    // First check for image column (always center)
    if (isImageColumn(columnIndex)) {
      return 'center';
    }
    
    // Check llmPayload for role-based alignment
    if (llmPayload?.columns && Array.isArray(llmPayload.columns) && data?.headers?.[columnIndex]) {
      const headerName = data.headers[columnIndex].toString();
      const column = llmPayload.columns.find(
        (col) => col.name?.toUpperCase() === headerName?.toUpperCase()
      );
      
      if (column?.role) {
        // measure = right alignment, dimension = left alignment
        return column.role === 'measure' ? 'right' : 'left';
      }
    }
    
    // Fallback to existing keyword-based detection if no llmPayload
    return isCurrencyOrNumericColumn(columnIndex) ? 'right' : 'left';
  }, [llmPayload, data?.headers]);

  const isCurrencyOrNumericColumn = (columnIndex: number) => {
    if (!data?.headers?.[columnIndex]) return false;
    
    const header = data.headers[columnIndex].toString().toLowerCase();
    
    const currencyKeywords = ['currency', 'amount', 'total', 'revenue', 'profit', 'loss', 'cost', 'price', 'value', 'balance', 'fee', 'tax', 'income', 'expense', 'spend', 'roas', 'prior period', 'prior_period', 'pp'];
    if (currencyKeywords.some(keyword => header.includes(keyword))) {
      return true;
    }
    
    return isNumericColumn(columnIndex);
  };

  const isImageUrl = (value: any) => {
    if (!value || typeof value !== 'string') return false;
    const url = value.toLowerCase().trim();
    return (
      url.startsWith('http://') || 
      url.startsWith('https://') || 
      url.startsWith('//')
    ) && (
      url.includes('.jpg') || 
      url.includes('.jpeg') || 
      url.includes('.png') || 
      url.includes('.gif') || 
      url.includes('.webp') || 
      url.includes('.svg')
    );
  };

  const renderCellContent = (cell: any, columnIndex: number) => {
    let formattedValue = cell;
    
    // For measure columns, format with commas and limit decimals to 2
    if (isMeasureColumn(columnIndex)) {
      const cellStr = String(cell).trim().toLowerCase();
      
      // Skip if null/none/empty or percentage
      if (cellStr === 'none' || cellStr === 'null' || cellStr === '' || cellStr.includes('%')) {
        formattedValue = cellStr === 'none' || cellStr === 'null' ? '' : cell;
      } else {
        // Remove commas for parsing
        const cleanedValue = String(cell).trim().replace(/[,$%\s]/g, '');
        const numValue = parseFloat(cleanedValue);
        
        if (!isNaN(numValue) && cleanedValue !== '') {
          // Limit to 2 decimal places if more than 2
          let formattedNum: string;
          const decimalPart = cleanedValue.split('.')[1];
          if (decimalPart && decimalPart.length > 2) {
            formattedNum = numValue.toFixed(2);
          } else {
            formattedNum = numValue.toString();
          }
          
          // Format with commas
          const parts = formattedNum.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          formattedValue = parts.join('.');
        } else {
          formattedValue = formatCellValue(cell, columnIndex);
        }
      }
    } else {
      // For non-measure columns, use the passed formatCellValue
      formattedValue = formatCellValue(cell, columnIndex);
    }
    
    // Check if this is an image column and the value is an image URL
    if (isImageColumn(columnIndex) && isImageUrl(cell)) {
      return (
        <div className="flex items-center justify-center">
          <img 
            src={String(cell).trim()} 
            alt="Product" 
            className="w-12 h-12 object-cover rounded border"
            onError={(e) => {
              // Fallback to text if image fails to load
              const img = e.currentTarget as HTMLImageElement;
              const nextSibling = img.nextElementSibling as HTMLElement;
              if (img && nextSibling) {
                img.style.display = 'none';
                nextSibling.style.display = 'block';
              }
            }}
          />
          <span style={{ display: 'none' }} className="text-xs text-gray-500 truncate">
            {formattedValue}
          </span>
        </div>
      );
    }
    
    return (
      <Tooltip message={String(formattedValue)}>
        <div 
          className="truncate" 
          style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px',
            color: '#616161'
          }}
        >
          {formattedValue}
        </div>
      </Tooltip>
    );
  };
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [allRows, setAllRows] = useState<any[][]>([]);

//   useEffect(()=>{
//     setIsLoadingMore(loading)
//   },[loading])

  useEffect(() => {
    if (data?.rows) {
      setAllRows(data.rows);
      setCurrentPage(request?.page || 1);
      setHasMoreData(true);
      // console.log('Initialized with:', {
      //   rowCount: data.rows.length,
      //   currentPage: request?.page || 1,
      //   hasMoreData: true
      // });
    }
  }, [data, request?.page]);

    const handleSort = (columnIndex: number) => {
    setSortConfig(prev => {
      if (prev?.column === columnIndex) {
        return { column: columnIndex, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnIndex, direction: 'asc' };
    });
  };

    if (sortConfig) {
    const { column, direction } = sortConfig;
    filteredAndSortedRows.sort((a, b) => {
      const valA = a[column];
      const valB = b[column];
      
      // Check for empty values
      const isEmptyA = valA === null || valA === undefined || valA === '' || 
                       String(valA).toLowerCase() === 'null';
      const isEmptyB = valB === null || valB === undefined || valB === '' || 
                       String(valB).toLowerCase() === 'null';
      
      // For numeric columns: empty values always go to bottom
      if (isNumericColumn(column)) {
        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA) return 1;  // A goes to bottom
        if (isEmptyB) return -1; // B goes to bottom
        
        // Parse as numbers for proper numeric sorting
        const numA = parseFloat(String(valA).replace(/[,$%\s]/g, ''));
        const numB = parseFloat(String(valB).replace(/[,$%\s]/g, ''));
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return direction === 'asc' ? numA - numB : numB - numA;
        }
      }
      
      // For non-numeric columns: use string comparison
      const formattedA = String(formatCellValue(valA, column)).toLowerCase();
      const formattedB = String(formatCellValue(valB, column)).toLowerCase();
      
      if (formattedA < formattedB) return direction === 'asc' ? -1 : 1;
      if (formattedA > formattedB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }


  useEffect(()=>{
    if(tableRows && tableRows.data.rows){
        const updatedRows = [...allRows, ...tableRows.data.rows];
        setAllRows(updatedRows);
        setCurrentPage(tableRows.page);
        setHasMoreData(tableRows.has_more);

        // const updatedData: TableData = {
        //   ...data,
        //   rows: updatedRows
        // };
        // onDataUpdate(updatedData);
    }
  }, [tableRows])

  const loadMoreData = useCallback(async () => {
    if (!request || isLoadingMore || !hasMoreData || loading) return;

    // Skip API call if we have less than 10 records (no need for pagination)
    if (allRows.length < 10) {
      console.log('Skipping API call - less than 10 records:', allRows.length);
      setHasMoreData(false);
      return;
    }

   // console.log('Loading more data - Page:', currentPage + 1);
    setIsLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      const newRequest: TableRequest = {
        ...request,
        page: nextPage,
        page_size: request.page_size || 50
      };

      console.log('Making API call with request:', newRequest);
      const response = await getTableDataRequest(newRequest);
      console.log('API response:', response);

    } catch (error) {
      console.error('Error loading more data:', error);
      setHasMoreData(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [request, currentPage, isLoadingMore, hasMoreData, allRows.length, getTableDataRequest]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || isLoadingMore || !hasMoreData) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // console.log('Scroll detected:', {
    //   scrollTop,
    //   scrollHeight,
    //   clientHeight,
    //   scrollPercentage,
    //   isLoadingMore,
    //   hasMoreData,
    //   globalLoading: loading
    // });

    if (scrollPercentage >= 0.85) {
     // console.log('Triggering load more data');
      loadMoreData();
    }
  }, [loadMoreData, isLoadingMore, hasMoreData]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const throttledHandleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100); 
    };

   // console.log('Adding scroll listener');
    scrollContainer.addEventListener('scroll', throttledHandleScroll);
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      scrollContainer.removeEventListener('scroll', throttledHandleScroll);
      console.log('Removing scroll listener');
    };
  }, [handleScroll]);

  useEffect(() => {
    if (isOpen && data?.rows) {
      setAllRows(data.rows);
      setCurrentPage(request?.page || 1);
      setHasMoreData(true);
      setIsLoadingMore(false);
    }
  }, [isOpen, data, request?.page]);

  useEffect(() => {
    if (isOpen && allRows.length > 0 && hasMoreData && !isLoadingMore) {
      // Skip if we have less than 10 records
      if (allRows.length < 10) {
        console.log('Skipping initial load - less than 10 records:', allRows.length);
        setHasMoreData(false);
        return;
      }

      setTimeout(() => {
        if (scrollContainerRef.current) {
          const { scrollHeight, clientHeight } = scrollContainerRef.current;
          console.log('Initial content check:', { scrollHeight, clientHeight, loading });
          if (scrollHeight <= clientHeight) {
            console.log('Content too small, loading more data immediately');
            loadMoreData();
          }
        }
      }, 100);
    }
  }, [isOpen, allRows.length, hasMoreData, isLoadingMore, loadMoreData]);

  if (!isOpen || !portalContainer) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-white flex flex-col" 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
        backgroundColor: 'white'
      }}
    >
      {/* Modal Header with Applied Filters */}
      <div className="p-4 border-b border-gray-200 bg-white shadow-sm flex justify-end">
        {/* Applied Filters Display */}
        {sqlQuery && (
          <div className="flex justify-end">
            <AppliedFiltersDisplay
              sqlQuery={sqlQuery}
              onFilterValueChange={onFilterValueChange}
              onFilterUpdateComplete={onFilterUpdateComplete}
              customerId={customerId}
              filterData={filterData}
              messageId={messageId}
              hasChartSwitcher={false}
              pdfGeneration={false}
              onExportExcel={onExportExcel}
              onDownloadPDF={onDownloadPDF}
              chartData={null}
              chartType="table"
              chartMetadata={null}
              onFullView={onClose}
              isInModal={true}
            />
          </div>
        )}
      </div>
      
      {/* Modal Content */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-auto"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="min-w-full">
          <table className="w-full border-collapse text-LimedSpruce">
            <thead className="bg-white sticky top-0 z-10">
              <tr>
                {data.headers.map((header, index) => (
                  <th
                    key={index}
                    onClick={() => handleSort(index)}
                    className="border-b border-gray-200 cursor-pointer relative"
                    style={{ 
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '0px',
                      color: '#616161',
                      padding: '10px',
                      verticalAlign: 'top',
                      textAlign: getColumnAlignment(index)
                    }}
                  >
                    <div 
                      style={{ 
                        position: 'relative',
                        width: '100%',
                        paddingRight: sortConfig?.column === index ? '16px' : '0px',
                        alignSelf: 'flex-start'
                      }}
                    >
                      <div
                        style={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: '20px',
                          textAlign: getColumnAlignment(index)
                        }}
                      >
                        {getColumnDisplayName(String(header))}
                      </div>
                      {sortConfig?.column === index && (
                        <span 
                          style={{ 
                            position: 'absolute',
                            right: '0',
                            top: '0',
                            lineHeight: '20px'
                          }}
                        >
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${
                    rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } hover:bg-blue-50 transition-all duration-200`}
                >
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="border-b border-gray-100"
                      style={{ 
                        padding: '10px',
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0px',
                        color: '#616161',
                        textAlign: getColumnAlignment(cellIndex)
                      }}
                    >
                      {renderCellContent(cell, cellIndex)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          
         {(isLoadingMore || loading) && (
            <div className="flex justify-center items-center py-8 bg-white">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="text-sm text-gray-600">
                  {loading ? 'Loading data...' : `Loading page ${currentPage + 1}...`}
                </span>
              </div>
            </div>
          )}
          
          {!hasMoreData && allRows.length > 0 && !isLoadingMore && (
            <div className="flex justify-center items-center py-6 bg-gray-50">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.25} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-gray-500">All data loaded</span>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal Footer */}
      <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200 bg-white shadow-sm">
        <div className="flex items-center space-x-4">
          <span className="custom-border flex items-center px-4 py-1 text-xs text-primary hover:bg-[#e6e6e6] rounded transition-colors">
            Showing {allRows.length} of {allRows.length} rows
            {hasMoreData && !isLoadingMore && <span className="ml-1">(more available)</span>}
          </span>
          {isLoadingMore && (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
              <span className="text-xs text-gray-500">
                Loading page {currentPage + 1}...
              </span>
            </div>
          )}
        </div>
        <div className="flex space-x-2 ml-auto">
          <button
            onClick={onExport}
            className="custom-border flex items-center px-4 py-1 text-xs text-primary hover:bg-[#e6e6e6] rounded transition-colors"
            title="Export to Excel"
          >
            Export
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1 bg-primary text-white text-xs rounded hover:bg-[#0041a6] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, portalContainer);
};

export default TableFullViewModal;